import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import type { AdminCatalog, ReferenceImage } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { useDeleteReference, usePutReference, useReferencePreview } from '../../hooks';
import { getErrorDetail } from '../../utils/error';
import { CHECKERBOARD, formatBytes, formatKst } from './format';
import ReferenceViewer from './ReferenceViewer';

const MAX_BYTES = 8 * 1024 * 1024;
const NAME_RE = /^[a-z0-9][a-z0-9_.-]{0,127}$/;

/** 파일 이름 → 참조 이미지 이름 후보(소문자·하이픈). 서버 규칙을 통과하는 모양으로. */
const suggestName = (file: File): string =>
  file.name
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 128);

const reason = (error: unknown, t: (k: string) => string): string => {
  const body = presetError(error);
  if (body?.error_code === 'REFERENCE_IMAGE_INVALID') return t(`imagePresets.refReason.${body.detail}`);
  return getErrorDetail(error, t('imagePresets.saveFailed'));
};

const ReferenceCard: React.FC<{ image: ReferenceImage; canEdit: boolean }> = ({ image, canEdit }) => {
  const { t } = useTranslation();
  const url = useReferencePreview(image.name, image.sha256);
  const put = usePutReference();
  const remove = useDeleteReference();
  const inUse = image.used_by.length > 0;
  const [viewing, setViewing] = useState(false);

  const replace = (file: File | undefined) => {
    if (!file) return;
    const message = inUse
      ? t('imagePresets.confirmReplaceInUse', { count: image.used_by.length })
      : t('imagePresets.confirmReplace');
    if (window.confirm(message)) put.mutate({ name: image.name, file });
  };

  return (
    <li className="card overflow-hidden flex flex-col">
      <button
        type="button"
        onClick={() => setViewing(true)}
        title={t('imagePresets.viewReference')}
        className={`aspect-[4/3] ${CHECKERBOARD} flex items-center justify-center cursor-zoom-in`}
      >
        {url ? (
          <img src={url} alt={image.name} className="max-h-full max-w-full object-contain" />
        ) : (
          <Loader2 className="animate-spin text-slate-300" />
        )}
      </button>
      <div className="p-3 space-y-1.5 text-xs flex-1">
        <p className="font-mono text-sm text-slate-800 break-all">{image.name}</p>
        <p className="text-slate-500">
          {image.mime.replace('image/', '').toUpperCase()} · {formatBytes(image.size)} · {formatKst(image.created_at)}
          {image.created_by && ` · ${image.created_by}`}
        </p>
        <p className="text-slate-600">
          {inUse ? (
            <>
              {t('imagePresets.usedBy')}{' '}
              {image.used_by.map((id) => (
                <span key={id} className="font-mono mr-1.5">
                  {id}
                </span>
              ))}
            </>
          ) : (
            <span className="text-slate-400">{t('imagePresets.unused')}</span>
          )}
        </p>
        {(put.error || remove.error) && <p className="text-red-600">{reason(put.error ?? remove.error, t)}</p>}
      </div>
      <div className="flex border-t border-slate-100">
        {url && (
          <a
            href={url}
            download={image.name}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs text-slate-600 hover:bg-slate-50"
          >
            <Download size={13} /> {t('imagePresets.download')}
          </a>
        )}
        {canEdit && (
          <label className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs text-slate-600 hover:bg-slate-50 border-l border-slate-100 cursor-pointer">
            {put.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {t('imagePresets.replace')}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => replace(e.target.files?.[0])}
            />
          </label>
        )}
        {canEdit && (
          <button
            type="button"
            disabled={inUse || remove.isPending}
            title={inUse ? t('imagePresets.refInUse') : t('imagePresets.delete')}
            onClick={() =>
              window.confirm(t('imagePresets.confirmDeleteRef', { name: image.name })) && remove.mutate(image.name)
            }
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs text-slate-600 hover:bg-red-50 hover:text-red-600 border-l border-slate-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600"
          >
            <Trash2 size={13} /> {t('imagePresets.delete')}
          </button>
        )}
      </div>
      {viewing && <ReferenceViewer image={image} onClose={() => setViewing(false)} />}
    </li>
  );
};

const ReferenceImagesPanel: React.FC<{ catalog: AdminCatalog; canEdit: boolean }> = ({ catalog, canEdit }) => {
  const { t } = useTranslation();
  const put = usePutReference();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const exists = catalog.reference_images.some((r) => r.name === name);
  const tooBig = !!file && file.size > MAX_BYTES;
  const badName = !!name && !NAME_RE.test(name);

  const upload = async () => {
    if (!file) return;
    if (exists && !window.confirm(t('imagePresets.confirmReplace'))) return;
    try {
      await put.mutateAsync({ name, file });
      setFile(null);
      setName('');
    } catch {
      // 아래에 보인다.
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('imagePresets.referencesIntro')}</p>
      {canEdit && (
        <div className="card p-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="ref-file" className="block text-sm font-medium text-slate-700 mb-1">
              {t('imagePresets.refFile')}
            </label>
            <input
              id="ref-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f && !name) setName(suggestName(f));
              }}
              className="block text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
            />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <label htmlFor="ref-name" className="block text-sm font-medium text-slate-700 mb-1">
              {t('imagePresets.refName')}
            </label>
            <input
              id="ref-name"
              className="input font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="rumi-character.png"
            />
          </div>
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            onClick={upload}
            disabled={!file || !name || badName || tooBig || put.isPending}
          >
            {put.isPending ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {exists ? t('imagePresets.replace') : t('imagePresets.upload')}
          </button>
          <div className="basis-full text-xs space-y-0.5">
            {tooBig && <p className="text-red-600">{t('imagePresets.refReason.too_large')}</p>}
            {badName && <p className="text-red-600">{t('imagePresets.refReason.bad_name')}</p>}
            {exists && <p className="text-amber-700">{t('imagePresets.refExists')}</p>}
            {put.error && <p className="text-red-600">{reason(put.error, t)}</p>}
          </div>
        </div>
      )}
      {catalog.reference_images.length === 0 ? (
        <div className="card p-10 text-center text-sm text-slate-500">
          <ImageOff className="mx-auto mb-2 text-slate-300" />
          {t('imagePresets.noReferences')}
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {catalog.reference_images.map((image) => (
            <ReferenceCard key={image.name} image={image} canEdit={canEdit} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReferenceImagesPanel;
